import { Button } from '@crowdex/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import type { DivergedBranch } from '@/lib/desktop-shell/store';
import type { DivergedBranchChoice } from '@/lib/native-desktop';

function commits(count: number): string {
  return `${count} commit${count === 1 ? '' : 's'}`;
}

/**
 * Checking out a remote branch moves the local branch of the same name onto it. When that local
 * branch has commits the remote lacks, moving it would set them aside, so the user picks a side.
 */
export function DivergedBranchDialog({
  diverged,
  onChoose,
  onOpenChange,
  working,
}: {
  diverged: DivergedBranch | null;
  onChoose: (choice: DivergedBranchChoice) => void;
  onOpenChange: (open: boolean) => void;
  working: boolean;
}) {
  const branch = diverged?.branch ?? '';
  return (
    <Dialog onOpenChange={onOpenChange} open={diverged !== null}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Your local {branch} has its own commits</DialogTitle>
          <DialogDescription>
            The local branch {branch} has {commits(diverged?.ahead ?? 0)} that origin/{branch}{' '}
            doesn’t have
            {diverged !== null && diverged.behind > 0
              ? `, and origin has ${commits(diverged.behind)} it doesn’t have`
              : ''}
            . Keep your local branch, or reset it to origin’s version (your commits stay reachable
            in the reflog).
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="ghost">
            Cancel
          </Button>
          <Button disabled={working} onClick={() => onChoose('keep-local')} variant="outline">
            Keep local
          </Button>
          <Button disabled={working} onClick={() => onChoose('use-remote')}>
            Use origin/{branch}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
